import React, {useCallback, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {IAppliedRule} from '../../../incase-domain/appliedRules';
import Modal from '../../../incase-domain/components/modals/directory/Modal';
import {selectedPresentationSelector} from '../../../store/selectors/presentation.selectors';
import RulesFlowEditor, {RulesFlowEditorHandle} from './RulesFlowEditor';
import {useSaveAppliedRulesMutation} from '../../../hooks/useSaveAppliedRules';

interface RulesModalProps {
    isOpen: boolean;
    onClose: () => void;
}

function RulesModal({isOpen, onClose}: RulesModalProps): JSX.Element {
    const {mutate: saveAppliedRules, isLoading} = useSaveAppliedRulesMutation();
    const {t} = useTranslation();

    const presentation = useSelector(selectedPresentationSelector);
    const editorRef = useRef<RulesFlowEditorHandle>(null);

    const initialRules: IAppliedRule[] = presentation.appliedRules ?? [];

    const handleSave = useCallback(() => {
        const appliedRules = editorRef.current?.getRules() ?? [];
        saveAppliedRules(
            {appliedRules, presentationId: presentation._id},
            {
                onSuccess: (updated) => {
                    if (updated) {
                        toast.success(t('toasts.successfullySaved'));
                        onClose();
                    } else {
                        toast.error(t('appliedRules.error.updateRulesFail'));
                    }
                },
                onError: () => {
                    toast.error(t('appliedRules.error.updateRulesFail'));
                },
            }
        );
    }, [saveAppliedRules, presentation._id, onClose, t]);

    return (
        <Modal
            icon="solar:branching-paths-up-bold"
            header={t('presEditPage.rules.presentationRules')}
            description={t('presEditPage.rules.ruleDesc')}
            isOpen={isOpen}
            closeOnClickOutside={false}
            onCancel={onClose}
            onConfirm={handleSave}
            showCancel
            showConfirm
            confirmText={t('common.save')}
            confirmDisabled={isLoading}
            size="fit"
        >
            <div style={{minWidth: '80vw'}} className="u-p-3">
                <RulesFlowEditor ref={editorRef} initialRules={initialRules} isOpen={isOpen} />
            </div>
        </Modal>
    );
}

export default RulesModal;
